/* eslint-disable complexity */
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { logError } from "@faire/web--source/common/logging";
import findMessengerConversations from "@faire/web-api--source/endpoints/www/api/messenger/list-conversations/post";
import postStarConversation from "@faire/web-api--source/endpoints/www/api/messenger/star-unstar-conversation/post";
import fetchMessengerConversationByBrand from "@faire/web-api--source/endpoints/www/api/v3/messenger/brand-conversation/brandToken/get";
import fetchMessengerConversation from "@faire/web-api--source/endpoints/www/api/v3/messenger/conversation/conversationToken/get";
import fetchMessengerConversationByRetailer from "@faire/web-api--source/endpoints/www/api/v3/messenger/retailer-conversation/retailerToken/get";
import { trackMessengerMessageFromBrandToRetailer } from "@faire/web-api--source/events/messenger/actionUnknown/messageFromBrandToRetailer";
import { trackMessengerMessageFromRetailerToBrand } from "@faire/web-api--source/events/messenger/actionUnknown/messageFromRetailerToBrand";
import { IGetConversationResponseV3 } from "@faire/web-api--source/faire/messenger/IGetConversationResponseV3";
import { IGetMessengerConversationsRequest } from "@faire/web-api--source/indigofair/data/IGetMessengerConversationsRequest";
import { IGetMessengerConversationsResponse } from "@faire/web-api--source/indigofair/data/IGetMessengerConversationsResponse";
import { IMessengerConversation } from "@faire/web-api--source/indigofair/data/IMessengerConversation";
import { IMessengerMessage } from "@faire/web-api--source/indigofair/data/IMessengerMessage";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { IWebSocketMessage } from "@faire/web-api--source/indigofair/websockets/IWebSocketMessage";
import compact from "lodash/compact";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import uniqBy from "lodash/uniqBy";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  createContext,
  useContext,
} from "react";
import { createStore, useStore, StoreApi } from "zustand";

import { IMessageChannel, Message } from "../MessageChannel/MessageChannel";

import {
  ConversationModel,
  getConversationCreatedAt as getConversationModelCreatedAt,
  getConversationToken as getConversationModelToken,
  getConversationIdentifier as getConversationModelIdentifier,
  getConversationUserToken as getConversationModelUserToken,
  getConversationLatestMessage as getConversationModelLatestMessage,
  getConversationOtherPartyBrandToken as getConversationModelOtherPartyBrandToken,
  getConversationOtherPartyToken as getConversationModelOtherPartyUserToken,
  isConversationUnRead as isConversationModelUnRead,
  getConversationOtherPartyBusinessImage as getConversationModelOtherPartyBusinessImage,
  isConversationSupportConversation as isConversationModelSupportConversation,
  isConversationMarkedAsSpam as isConversationModelMarkedAsSpam,
  isConversationStarredByBrand as isConversationModelStarredByBrand,
  getConversationKey as getConversationModelKey,
  getConversationOrderedMessages as getConversationModelOrderedMessages,
  isConversationInputDisabled as isConversationModelInputDisabled,
  fetchAdditionalMessagesThrottled,
  getConversationOtherPartyUserType as getConversationModelOtherPartyUserType,
  getConversationParticipants as getConversationModelParticipants,
  openConversation,
  getConversationLastModified as getConversationModelLastModified,
  isMessageFromCurrentUser as isMessageModelFromCurrentUser,
  isMessageFromOtherUser as isMessageModelFromOtherUser,
  isMessageFromSameRetailerAsCurrentUser as isMessageModelFromSameRetailerAsCurrentUser,
  isMessageFromSameBrandAsCurrentUser as isMessageModelFromSameBrandAsCurrentUser,
  isMessageRead as isMessageModelRead,
  isMessageLatestReadFromCurrentUser as isMessageModelLatestReadFromCurrentUser,
  isMessageLatestDelivered as isMessageModelLatestDelivered,
  sendMessage as sendMessageModel,
  deleteMessage as deleteMessageModel,
  getMessageOtherPartyProfilePicUrl as getMessageModelOtherPartyProfilePicUrl,
  getMessageCurrentPartyProfilePicUrl as getMessageModelCurrentPartyProfilePicUrl,
  getMessageOtherPartyFirstName as getMessageModelOtherPartyFirstName,
  getConversationMessageCount as getConversationModelMessageCount,
  isConversationOtherPartyBrandUser as isConversationModelOtherPartyBrandUser,
  setIsStarredByBrand,
  uploadImagesAndSendNewMessage,
  fetchMessages,
  createSyntheticStatusMessage,
  createConversationModel,
  updateConversation as updateConversationModel,
  firstFetchOrUpdateMessages,
} from "./ConversationModel";
import {
  MessageModel,
  shouldPopUp as getMessageModelShouldPopUp,
  getToken as getMessageModelToken,
} from "./MessageModel";
import { MessengerPollingFallback } from "./MessengerPollingFallback";
import {
  FILTER_SUPPORT_CONVERSATION,
  MainStreamFetchState,
  createMainStreamFetchState,
  removeReadConversations,
  removeNonStarredByBrandConversations,
  setIsFirstFetch,
  setNextRequest,
  setLastMainStreamConversationRequestTokens,
} from "./MessengerStore";
import {
  getBrandTokenInSession,
  conversationRetailerContextComparator,
  conversationBrandContextComparator,
  conversationLastModifiedComparator,
} from "./utils";

/**
 * Note: All hooks in this file must support not having a `MessengerContext` provided, as there is no
 * such context in the visitor portal. The most correct way to implement a `useCallback` exported
 * from this file is to do an early return if `MessengerContext` is not provided (with a default
 * value like `false` for something with a return value like `hasConversationWithBrand`.)
 */

type MessengerContext = {
  hasInitialized: boolean;
  setHasInitialized: (hasInitialized: boolean) => void;

  user?: IUser;
  setUser: (user: IUser) => void;

  brand?: IGetMessengerConversationsResponse.ISimpleBrand;

  brandContext?: IGetMessengerConversationsResponse.ISimpleBrand;
  setBrandContext: (
    updater: (
      currentBrandContext?: IGetMessengerConversationsResponse.ISimpleBrand
    ) => IGetMessengerConversationsResponse.ISimpleBrand | undefined
  ) => void;

  latestMessageUpdatedAt?: number;
  setLatestMessageUpdatedAt: (
    latestMessageUpdatedAt: number | undefined
  ) => void;

  retailerContext?: IRetailer;
  setRetailerContext: (
    updater: (currentRetailerContext?: IRetailer) => IRetailer | undefined
  ) => void;

  query: string;
  setQuery: (query: string) => void;

  lastSearchQuery?: string;
  setLastSearchQuery: (lastSearchQuery: string | undefined) => void;

  searchResults?: Set<string>;
  setSearchResults: (searchResults: Set<string> | undefined) => void;

  allMainStreamFetchState: MainStreamFetchState;
  setAllMainStreamFetchState: (
    updater: (state: MainStreamFetchState) => MainStreamFetchState
  ) => void;

  unreadOnlyMainStreamFetchState: MainStreamFetchState;
  setUnreadOnlyMainStreamFetchState: (
    updater: (state: MainStreamFetchState) => MainStreamFetchState
  ) => void;

  starredByBrandOnlyMainStreamFetchState: MainStreamFetchState;
  setStarredByBrandOnlyMainStreamFetchState: (
    updater: (state: MainStreamFetchState) => MainStreamFetchState
  ) => void;

  currentConversationIdentifier?: string;
  setCurrentConversationIdentifier: (
    currentConversationIdentifier: string | undefined
  ) => void;

  isLoadingCount: number;
  incrementIsLoadingCount: () => void;
  decrementIsLoadingCount: () => void;

  showOnlyUnreadConversations: boolean;
  setShowOnlyUnreadConversations: (
    showOnlyUnreadConversations: boolean
  ) => void;

  showOnlyStarredByBrandConversations: boolean;
  setShowOnlyStarredByBrandConversations: (
    showOnlyStarredByBrandConversations: boolean
  ) => void;

  hiddenConversations: string[];

  totalUnreadConversations?: number;
  setTotalUnreadConversations: (
    totalUnreadConversations: number | undefined
  ) => void;

  totalStarredByBrandConversations?: number;
  setTotalStarredByBrandConversations: (
    totalStarredByBrandConversations: number | undefined
  ) => void;

  wasConnected: boolean;
  setWasConnected: (wasConnected: boolean) => void;

  pollingFallback?: MessengerPollingFallback;
  setPollingFallback: (pollingFallback: MessengerPollingFallback) => void;

  reset: () => void;
};

const MessengerStoreContext = createContext<
  StoreApi<MessengerContext> | undefined
>(undefined);

// This long type is unfortunately necessary so that `create<MessengerContext>` will typecheck.
const initialState: Pick<
  MessengerContext,
  | "hasInitialized"
  | "user"
  | "brand"
  | "brandContext"
  | "latestMessageUpdatedAt"
  | "retailerContext"
  | "query"
  | "lastSearchQuery"
  | "allMainStreamFetchState"
  | "unreadOnlyMainStreamFetchState"
  | "starredByBrandOnlyMainStreamFetchState"
  | "currentConversationIdentifier"
  | "isLoadingCount"
  | "showOnlyUnreadConversations"
  | "showOnlyStarredByBrandConversations"
  | "hiddenConversations"
  | "totalUnreadConversations"
  | "totalStarredByBrandConversations"
  | "wasConnected"
  | "pollingFallback"
> = {
  hasInitialized: false,
  user: undefined,
  brand: undefined,
  brandContext: undefined,
  latestMessageUpdatedAt: undefined,
  retailerContext: undefined,
  query: "",
  lastSearchQuery: undefined,
  allMainStreamFetchState: createMainStreamFetchState(),
  unreadOnlyMainStreamFetchState: createMainStreamFetchState(),
  starredByBrandOnlyMainStreamFetchState: createMainStreamFetchState(),
  currentConversationIdentifier: undefined,
  isLoadingCount: 0,
  showOnlyUnreadConversations: false,
  showOnlyStarredByBrandConversations: false,
  hiddenConversations: [],
  totalUnreadConversations: undefined,
  totalStarredByBrandConversations: undefined,
  wasConnected: false,
  pollingFallback: undefined,
};

const createMessengerContext = () =>
  createStore<MessengerContext>((set) => {
    return {
      ...initialState,
      setHasInitialized: (hasInitialized: boolean) => set({ hasInitialized }),
      setUser: (user: IUser) => set({ user }),
      setBrandContext: (
        updater: (
          currentBrandContext?: IGetMessengerConversationsResponse.ISimpleBrand
        ) => IGetMessengerConversationsResponse.ISimpleBrand | undefined
      ) => set((state) => ({ brandContext: updater(state.brandContext) })),
      setLatestMessageUpdatedAt: (latestMessageUpdatedAt: number | undefined) =>
        set({ latestMessageUpdatedAt }),
      setRetailerContext: (
        updater: (currentRetailerContext?: IRetailer) => IRetailer | undefined
      ) =>
        set((state) => ({ retailerContext: updater(state.retailerContext) })),
      setQuery: (query: string) => set({ query }),
      setLastSearchQuery: (lastSearchQuery: string | undefined) =>
        set({ lastSearchQuery }),
      setAllMainStreamFetchState: (updater) =>
        set((state) => ({
          allMainStreamFetchState: updater(state.allMainStreamFetchState),
        })),
      setUnreadOnlyMainStreamFetchState: (updater) =>
        set((state) => ({
          unreadOnlyMainStreamFetchState: updater(
            state.unreadOnlyMainStreamFetchState
          ),
        })),
      setStarredByBrandOnlyMainStreamFetchState: (updater) =>
        set((state) => ({
          starredByBrandOnlyMainStreamFetchState: updater(
            state.starredByBrandOnlyMainStreamFetchState
          ),
        })),
      setSearchResults: (searchResults: Set<string> | undefined) =>
        set({ searchResults }),
      setCurrentConversationIdentifier: (
        currentConversationIdentifier: string | undefined
      ) => set({ currentConversationIdentifier }),
      incrementIsLoadingCount: () =>
        set((state) => ({ isLoadingCount: state.isLoadingCount + 1 })),
      decrementIsLoadingCount: () =>
        set((state) => ({ isLoadingCount: state.isLoadingCount - 1 })),
      setShowOnlyUnreadConversations: (showOnlyUnreadConversations: boolean) =>
        set({ showOnlyUnreadConversations }),
      setShowOnlyStarredByBrandConversations: (
        showOnlyStarredByBrandConversations: boolean
      ) => set({ showOnlyStarredByBrandConversations }),
      setTotalUnreadConversations: (
        totalUnreadConversations: number | undefined
      ) => set({ totalUnreadConversations }),
      setTotalStarredByBrandConversations: (
        totalStarredByBrandConversations: number | undefined
      ) => set({ totalStarredByBrandConversations }),
      setWasConnected: (wasConnected: boolean) => set({ wasConnected }),
      setPollingFallback: (pollingFallback: MessengerPollingFallback) =>
        set({ pollingFallback }),
      reset: () => set(initialState),
    };
  });

const useMessengerContext = () => {
  const store = useContext(MessengerStoreContext);
  if (store) {
    // This hook is called conditionally here, but in practice it's unconditional, as every consumer of this
    // hook is either the brand or retailer portal (which always provides a messenger store context provider
    // in the JSX tree) or is the visitor portal (which never provides it.)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useStore(store);
  } else {
    return undefined;
  }
};

export const MessengerStoreContextProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const storeRef = useRef<StoreApi<MessengerContext> | undefined>(undefined);
  if (!storeRef.current) {
    storeRef.current = createMessengerContext();
  }
  return (
    <MessengerStoreContext.Provider value={storeRef.current}>
      <MessengerStoreEffectInitializers>
        {children}
      </MessengerStoreEffectInitializers>
    </MessengerStoreContext.Provider>
  );
};

const MessengerStoreEffectInitializers: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const {
    query,
    setLastSearchQuery,
    lastSearchQuery,
    brandContext,
    retailerContext,
  } = useMessengerContext() ?? {};
  const { searchConversations } = useMessengerStoreConversationFetching();

  // Capture via ref, because recomputing the memo when `searchConversations`
  // changes creates a new `debounce` and hence doesn't actually debounce it.
  const searchConversationsRef = useRef(searchConversations);
  searchConversationsRef.current = searchConversations;
  const searchConversationsDebounced = useMemo(
    () =>
      debounce(
        (
          params: Pick<
            IGetMessengerConversationsRequest,
            "context_token" | "query"
          >
        ) => searchConversationsRef.current(params),
        200
      ),
    []
  );

  const contextToken = brandContext?.token ?? retailerContext?.token;
  useEffect(() => {
    if (!setLastSearchQuery) {
      return;
    }

    if ((query ?? "") !== (lastSearchQuery ?? "")) {
      searchConversationsDebounced({
        query: query || undefined,
        context_token: contextToken,
      });
      setLastSearchQuery(query);
    }
  }, [
    query,
    lastSearchQuery,
    searchConversationsDebounced,
    contextToken,
    setLastSearchQuery,
  ]);

  return children;
};

export const useMessengerStoreConversationList = (): {
  latestUnreadConversationOtherPartyFirstName: string | undefined;
  orderedConversationIdentifiers: string[];
  hasConversations: boolean;
  isLoadingConversations: boolean;
  unreadConversationCount: number;
  setTotalUnreadConversations: (count: number | undefined) => void;
  starredByBrandConversationCount: number;
  showOnlyUnreadConversations: boolean;
  showOnlyStarredByBrandConversations: boolean;
  setShowOnlyUnreadConversations: (value: boolean) => Promise<void>;
  setShowOnlyStarredByBrandConversations: (value: boolean) => Promise<void>;
  setSearch: (search: string) => void;
  nextConversations: () => Promise<void>;
  query: string;
  hasConversationsNextRequest: boolean;
} => {
  const { hasConversations } = useMessengerStoreHasConversations();
  const { isLoadingConversations } = useMessengerStoreIsLoadingConversations();
  const { orderedConversations } = useMessengerStoreOrderedConversations();

  const {
    setBrandContext,
    setRetailerContext,
    totalUnreadConversations,
    totalStarredByBrandConversations,
    setTotalUnreadConversations: setTotalUnreadConversationsInContext,
    query,
    showOnlyUnreadConversations,
    showOnlyStarredByBrandConversations,
    setQuery,
    setShowOnlyUnreadConversations: setShowOnlyUnreadConversationsInContext,
    setShowOnlyStarredByBrandConversations:
      setShowOnlyStarredByBrandConversationsInContext,
    setUnreadOnlyMainStreamFetchState,
    setStarredByBrandOnlyMainStreamFetchState,
  } = useMessengerContext() ?? {};

  const {
    nextConversations,
    hasConversationsNextRequest,
    firstFetchOrUpdateConversations,
  } = useMessengerStoreConversationFetching();

  const { getConversationIsUnread, getConversationOtherPartyFirstName } =
    useMessengerStoreGetConversation();

  const unreadConversationCount = totalUnreadConversations ?? 0;
  const starredByBrandConversationCount = totalStarredByBrandConversations ?? 0;

  const setSearch = useCallback(
    (search: string) => {
      if (!setQuery || !setBrandContext || !setRetailerContext) {
        return;
      }

      setQuery(search);
      if (search) {
        setBrandContext(() => undefined);
        setRetailerContext(() => undefined);
      }
    },
    [setQuery, setBrandContext, setRetailerContext]
  );

  const memoizedOrderedConversationIdentifiers = useRef<string[]>([]);

  // This cannot be memoized (yet) because there's no way to specify the dependencies
  // on `orderedConversations`, as it's still updated via mutation. However this itself
  // will be re-created on update so can be used as a reactive dependency to other
  // useMemo etc.
  const orderedConversationIdentifiers = useMemo(() => {
    const newOrderedConversationIdentifiers = orderedConversations.map(
      (conversation) => getConversationModelIdentifier(conversation) ?? ""
    );

    if (
      !isEqual(
        newOrderedConversationIdentifiers,
        memoizedOrderedConversationIdentifiers.current
      )
    ) {
      memoizedOrderedConversationIdentifiers.current =
        newOrderedConversationIdentifiers;
      return newOrderedConversationIdentifiers;
    } else {
      return memoizedOrderedConversationIdentifiers.current;
    }
  }, [orderedConversations]);

  const latestUnreadConversationToken: string | undefined =
    orderedConversationIdentifiers.find((conversationIdentifier: string) =>
      getConversationIsUnread(conversationIdentifier)
    );

  const latestUnreadConversationOtherPartyFirstName: string | undefined =
    latestUnreadConversationToken
      ? getConversationOtherPartyFirstName(latestUnreadConversationToken)
      : undefined;

  const setShowOnlyUnreadConversations = useCallback(
    async (showOnlyUnreadConversations: boolean) => {
      if (
        !setShowOnlyUnreadConversationsInContext ||
        !setUnreadOnlyMainStreamFetchState ||
        !firstFetchOrUpdateConversations
      ) {
        return;
      }

      setShowOnlyUnreadConversationsInContext(showOnlyUnreadConversations);
      if (showOnlyUnreadConversations) {
        setUnreadOnlyMainStreamFetchState(removeReadConversations);
      }
      return firstFetchOrUpdateConversations();
    },
    [
      setUnreadOnlyMainStreamFetchState,
      firstFetchOrUpdateConversations,
      setShowOnlyUnreadConversationsInContext,
    ]
  );

  const setShowOnlyStarredByBrandConversations = useCallback(
    async (showOnlyStarredByBrandConversations: boolean) => {
      if (
        !setShowOnlyStarredByBrandConversationsInContext ||
        !setStarredByBrandOnlyMainStreamFetchState ||
        !firstFetchOrUpdateConversations
      ) {
        return;
      }

      setShowOnlyStarredByBrandConversationsInContext(
        showOnlyStarredByBrandConversations
      );
      if (showOnlyStarredByBrandConversations) {
        setStarredByBrandOnlyMainStreamFetchState(
          removeNonStarredByBrandConversations
        );
      }
      return firstFetchOrUpdateConversations();
    },
    [
      setStarredByBrandOnlyMainStreamFetchState,
      firstFetchOrUpdateConversations,
      setShowOnlyStarredByBrandConversationsInContext,
    ]
  );

  const setTotalUnreadConversations = useCallback(
    (count: number | undefined) => {
      setTotalUnreadConversationsInContext?.(count);
    },
    [setTotalUnreadConversationsInContext]
  );

  // Note: `...` spread syntax misses some fields here, not sure why, but we
  // list each field explicitly to prevent this.
  return {
    latestUnreadConversationOtherPartyFirstName,
    orderedConversationIdentifiers,
    hasConversations,
    isLoadingConversations,
    unreadConversationCount,
    setTotalUnreadConversations,
    starredByBrandConversationCount,
    showOnlyUnreadConversations: showOnlyUnreadConversations ?? false,
    showOnlyStarredByBrandConversations:
      showOnlyStarredByBrandConversations ?? false,
    setShowOnlyUnreadConversations,
    setShowOnlyStarredByBrandConversations,
    setSearch,
    nextConversations,
    query: query ?? "",
    hasConversationsNextRequest,
  };
};

export const useMessengerStoreInit = () => {
  const { setUser, setHasInitialized, hasInitialized } =
    useMessengerContext() ?? {};
  const { handleNewMessage } = useMessengerStoreConnectionManagement();
  const { firstFetchOrUpdateConversations } =
    useMessengerStoreConversationFetching();

  // It's important that we store `handleNewMessage` in a ref so that the
  // `messageChannel` event callback always invokes the freshest version of
  // it, instead of whatever version we might otherwise close over in the
  // definition of `init`.
  const handleNewMessageRef =
    useRef<(message: Message) => void>(handleNewMessage);
  handleNewMessageRef.current = handleNewMessage;

  const init = useCallback(
    async (user: IUser, messageChannel: IMessageChannel | undefined) => {
      if (!setUser || !setHasInitialized || !firstFetchOrUpdateConversations) {
        return;
      }

      if (hasInitialized) {
        return;
      }
      setHasInitialized(true);

      setUser(user);
      messageChannel?.subscribe((message) =>
        handleNewMessageRef.current(message)
      );

      await firstFetchOrUpdateConversations(user);
    },
    [
      firstFetchOrUpdateConversations,
      hasInitialized,
      setHasInitialized,
      setUser,
    ]
  );

  return useMemo(() => ({ init }), [init]);
};

export const useMessengerStoreConnectionManagement = () => {
  /**
   * These callbacks MUST support an undefined `store` at least when they
   * are defined, as they are called by `init` which is brought in to the
   * visitor package.
   */
  const { pollingFallback, setPollingFallback, wasConnected, setWasConnected } =
    useMessengerContext() ?? {};

  const {
    firstFetchOrUpdateConversations,
    createOrUpdateConversation,
    firstFetchOrUpdateMessagesInCurrentConversation,
  } = useMessengerStoreConversationFetching();

  const handleConnectionMessage = useCallback(
    (isConnected: boolean) => {
      if (
        !setWasConnected ||
        !setPollingFallback ||
        !firstFetchOrUpdateConversations ||
        !firstFetchOrUpdateMessagesInCurrentConversation
      ) {
        return;
      }

      if (isConnected) {
        if (!wasConnected) {
          firstFetchOrUpdateConversations();
          firstFetchOrUpdateMessagesInCurrentConversation();
        }
        setWasConnected(true);
        if (pollingFallback) {
          pollingFallback.stop();
        }
      } else {
        if (!pollingFallback) {
          setPollingFallback(
            new MessengerPollingFallback(() => {
              firstFetchOrUpdateConversations();
              firstFetchOrUpdateMessagesInCurrentConversation();
            }, !!wasConnected)
          );
        }
      }
    },
    [
      wasConnected,
      setWasConnected,
      pollingFallback,
      setPollingFallback,
      firstFetchOrUpdateConversations,
      firstFetchOrUpdateMessagesInCurrentConversation,
    ]
  );

  const handleNewMessage = useCallback(
    (message: Message) => {
      if (
        message.type === IWebSocketMessage.Type.CONVERSATION_UPDATED &&
        message.updated_conversation
      ) {
        createOrUpdateConversation(message.updated_conversation);
      }

      if (message.type === "CONNECTION") {
        handleConnectionMessage(message.isConnected);
      }
    },
    [createOrUpdateConversation, handleConnectionMessage]
  );

  return useMemo(
    () => ({
      handleConnectionMessage,
      handleNewMessage,
    }),
    [handleConnectionMessage, handleNewMessage]
  );
};

// Conversation Management Hooks
export const useMessengerStoreConversationManagement = () => {
  const { starredByBrandConversationCount } =
    useMessengerStoreConversationList();
  const { setTotalStarredByBrandConversations, hiddenConversations } =
    useMessengerContext() ?? {};
  const { storeUpdatedConversation } =
    useMessengerStoreStoreUpdatedConversation();
  const { firstFetchOrUpdateConversations } =
    useMessengerStoreConversationFetching();
  const { getConversationByIdentifier } =
    useMessengerStoreConversationIdentification();

  const setConversationIsStarredByBrand = useCallback(
    async (conversationToken: string, isStarred: boolean) => {
      if (
        !setTotalStarredByBrandConversations ||
        !getConversationByIdentifier ||
        !storeUpdatedConversation
      ) {
        return;
      }

      // Update the number of starred conversations immediately
      if (isStarred) {
        setTotalStarredByBrandConversations(
          starredByBrandConversationCount + 1
        );
      } else {
        setTotalStarredByBrandConversations(
          Math.max(0, starredByBrandConversationCount - 1)
        );
      }

      // Immediately updates star icon locally
      const conversation = getConversationByIdentifier(conversationToken);
      if (conversation) {
        const updatedConversation = setIsStarredByBrand(
          conversation,
          isStarred
        );
        storeUpdatedConversation(updatedConversation);
      }

      // Submit the endpoint to actually update the conversation, and
      // re-fetch the conversations, to align with backend state.
      await postStarConversation({
        conversation_token: conversationToken,
        star: isStarred,
      });
      await firstFetchOrUpdateConversations();
    },
    [
      getConversationByIdentifier,
      firstFetchOrUpdateConversations,
      setTotalStarredByBrandConversations,
      starredByBrandConversationCount,
      storeUpdatedConversation,
    ]
  );

  const toggleConversationIsStarredByBrand = useCallback(
    async (conversationToken: string) => {
      const conversation = getConversationByIdentifier(conversationToken);
      if (!conversation) {
        throw new Error(`conversation not found: ${conversationToken}`);
      }

      await setConversationIsStarredByBrand(
        conversationToken,
        !isConversationModelStarredByBrand(conversation)
      );
    },
    [getConversationByIdentifier, setConversationIsStarredByBrand]
  );

  const hideConversation = useCallback(
    (conversationToken: string) => {
      if (hiddenConversations) {
        hiddenConversations.push(conversationToken);
      }
    },
    [hiddenConversations]
  );

  const fetchConversationMessages = useCallback(
    async (conversationToken: string) => {
      const conversation = getConversationByIdentifier(conversationToken);
      if (conversation) {
        const updatedConversation = await fetchMessages(conversation);
        storeUpdatedConversation(updatedConversation);
      }
    },
    [getConversationByIdentifier, storeUpdatedConversation]
  );

  const sendMessageInConversation = useCallback(
    async (
      conversationIdentifier: string,
      message: Partial<IMessengerMessage>,
      images: readonly File[] | undefined,
      products: string[] = []
    ): Promise<void> => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        throw new Error("No conversation found for " + conversationIdentifier);
      }

      try {
        const messageData = {
          text: message.text ?? "",
          images: message.images ?? [],
        };

        const updatedConversation = await uploadImagesAndSendNewMessage(
          conversation,
          messageData,
          images,
          products
        );
        storeUpdatedConversation(updatedConversation);
      } catch (e) {
        throw new Error("Message sending failed: " + e);
      }
    },
    [getConversationByIdentifier, storeUpdatedConversation]
  );

  return useMemo(
    () => ({
      setConversationIsStarredByBrand,
      toggleConversationIsStarredByBrand,
      hideConversation,
      fetchConversationMessages,
      sendMessageInConversation,
      firstFetchOrUpdateConversations,
    }),
    [
      setConversationIsStarredByBrand,
      toggleConversationIsStarredByBrand,
      hideConversation,
      fetchConversationMessages,
      sendMessageInConversation,
      firstFetchOrUpdateConversations,
    ]
  );
};

// Context Management Hooks
export const useMessengerStoreUser = () => {
  const { user } = useMessengerContext() ?? {};
  const isBrandUser = user?.type === IUser.Type.BRAND_USER;
  return useMemo(() => ({ isBrandUser }), [isBrandUser]);
};

export const useMessengerStoreRetailerContext = () => {
  const { setQuery, setRetailerContext } = useMessengerContext() ?? {};

  const { fetchConversations } = useMessengerStoreConversationFetching();
  const { removeRetailerContextConversationIfEmpty } =
    useMessengerStoreStoreUpdatedConversation();

  /**
   * Sets the conversation as the first conversation in the list.
   *
   * Useful when you're on a page that refers to this conversation
   * ie. retailer page, contact, retailer order
   */
  const updateCurrentRetailerContext = useCallback(
    async (retailer?: IRetailer) => {
      if (
        !setRetailerContext ||
        !setQuery ||
        !removeRetailerContextConversationIfEmpty ||
        !fetchConversations
      ) {
        return;
      }

      let didChangeRetailerContext = false;
      let previousRetailerToken = undefined;
      let newContextToken = undefined;
      setRetailerContext((existingRetailerContext) => {
        if (retailer?.token === existingRetailerContext?.token) {
          previousRetailerToken = existingRetailerContext?.token;
          newContextToken = existingRetailerContext?.token;
          return existingRetailerContext;
        }

        previousRetailerToken = existingRetailerContext?.token;
        didChangeRetailerContext = true;
        newContextToken = retailer?.token;
        return retailer;
      });

      // If we have a retailer context that we're switching from, see if the
      // conversation has no messages. If so, remove it.
      // Don't check conversationByConversationToken because a conversation
      // has to have messages to have a conversation_token
      if (previousRetailerToken && didChangeRetailerContext) {
        removeRetailerContextConversationIfEmpty(previousRetailerToken);
      }

      if (didChangeRetailerContext) {
        setQuery("");

        // Only bother to re-fetch conversations if we have a new context
        // token, because otherwise the set of conversations will still
        // be valid (a subset of the previous list that had the retailer
        // context active.)
        if (newContextToken) {
          await fetchConversations({
            context_token: newContextToken,
          });
        }
      }
    },
    [
      fetchConversations,
      setRetailerContext,
      setQuery,
      removeRetailerContextConversationIfEmpty,
    ]
  );

  return useMemo(
    () => ({ updateCurrentRetailerContext }),
    [updateCurrentRetailerContext]
  );
};

export const useMessengerStoreBrandContext = () => {
  const { setQuery, setBrandContext } = useMessengerContext() ?? {};

  const { fetchConversations } = useMessengerStoreConversationFetching();
  const { removeBrandContextConversationIfEmpty } =
    useMessengerStoreStoreUpdatedConversation();

  /**
   * Sets the conversation as the first conversation in the list.
   *
   * Useful when you're on a page that refers to this conversation
   * ie. brand page, contact, brand order
   */
  const setCurrentBrandContext = useCallback(
    async (brand?: IGetMessengerConversationsResponse.ISimpleBrand) => {
      if (
        !setBrandContext ||
        !setQuery ||
        !removeBrandContextConversationIfEmpty ||
        !fetchConversations
      ) {
        return;
      }

      let didChangeBrandContext = false;
      let previousBrandToken = undefined;
      let newContextToken = undefined;
      setBrandContext((existingBrandContext) => {
        if (brand?.token === existingBrandContext?.token) {
          previousBrandToken = existingBrandContext?.token;
          newContextToken = existingBrandContext?.token;
          return existingBrandContext;
        }

        previousBrandToken = existingBrandContext?.token;
        if (brand?.hide_chat) {
          didChangeBrandContext = existingBrandContext !== undefined;
          return undefined;
        } else {
          didChangeBrandContext = true;
          newContextToken = brand?.token;
          return brand;
        }
      });

      // If we have a brand context that we're switching from, see if the
      // conversation has no messages. If so, remove it.
      // Don't check conversationByConversationToken because a conversation
      // has to have messages to have a conversation_token
      if (previousBrandToken && didChangeBrandContext) {
        removeBrandContextConversationIfEmpty(previousBrandToken);
      }

      if (didChangeBrandContext) {
        setQuery("");

        // Only bother to re-fetch conversations if we have a new context
        // token, because otherwise the set of conversations will still
        // be valid (a subset of the previous list that had the brand
        // context active.))
        if (newContextToken) {
          await fetchConversations({
            context_token: newContextToken,
          });
        }
      }
    },
    [
      fetchConversations,
      setBrandContext,
      setQuery,
      removeBrandContextConversationIfEmpty,
    ]
  );

  return useMemo(() => ({ setCurrentBrandContext }), [setCurrentBrandContext]);
};

export const isConversationToken = (token: string): boolean => {
  return token.startsWith("mc_");
};

export const useMessengerStoreGetConversation = () => {
  const { strictLocalize } = useStrictLocalization();
  const { storeUpdatedConversation } =
    useMessengerStoreStoreUpdatedConversation();
  const { setTotalUnreadConversations } = useMessengerContext() ?? {};
  const { getConversationByIdentifier } =
    useMessengerStoreConversationIdentification();
  const { conversationByRetailerToken, conversationByBrandToken } =
    useMessengerStoreConversationStorage();

  const hasConversation = useCallback(
    (conversationIdentifier: string) => {
      return !!getConversationByIdentifier?.(conversationIdentifier);
    },
    [getConversationByIdentifier]
  );

  const getConversationToken = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation ? getConversationModelToken(conversation) : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationCreatedAt = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelCreatedAt(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationLatestMessage = useCallback(
    (conversationIdentifier: string): MessageModel | undefined => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelLatestMessage(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationRetailer = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.retailer;
    },
    [getConversationByIdentifier]
  );

  const getConversationBrand = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.brand;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyType = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.otherParty
        ?.type;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyUserToken = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.otherParty
        ?.token;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyRetailerToken = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.otherParty
        ?.retailer_token;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyBrandToken = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelOtherPartyBrandToken(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsUnread = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation ? isConversationModelUnRead(conversation) : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyFirstName = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.otherParty
        ?.first_name;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyBusinessImage = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelOtherPartyBusinessImage(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsSupportConversation = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? isConversationModelSupportConversation(conversation)
        : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsMarkedAsSpam = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? isConversationModelMarkedAsSpam(conversation)
        : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsStarredByBrand = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? isConversationModelStarredByBrand(conversation)
        : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationUserToken = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelUserToken(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyCompanyName = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      if (!conversation) {
        return undefined;
      }

      let companyName = "";
      if (conversation.otherParty?.type === IUser.Type.ADMIN) {
        companyName = strictLocalize({
          defaultMessage: "Faire Support",
          description: {
            text: "Name of the other party when chatting with Faire Support",
          },
        });
      } else if (
        conversation.otherParty?.type === IUser.Type.USER &&
        conversation.retailer &&
        conversation.retailer.name
      ) {
        companyName = conversation.retailer.name;
      } else if (
        conversation.otherParty?.type === IUser.Type.BRAND_USER &&
        conversation.brand &&
        conversation.brand.name
      ) {
        companyName = conversation.brand.name;
      } else if (
        conversation.otherParty?.type === IUser.Type.GUEST &&
        conversation.otherPartyCompany
      ) {
        companyName = conversation.otherPartyCompany;
      }
      return companyName;
    },
    [getConversationByIdentifier, strictLocalize]
  );

  const getConversationIdentifier = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      if (!conversation) {
        return undefined;
      }

      return (
        getConversationModelToken(conversation) ??
        conversation.brand?.token ??
        conversation.retailer?.token ??
        getConversationModelUserToken(conversation)
      );
    },
    [getConversationByIdentifier]
  );

  const getConversationKey = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation ? getConversationModelKey(conversation) : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherParty = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.otherParty;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyProfileImage = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.otherParty
        ?.profile_image;
    },
    [getConversationByIdentifier]
  );

  const getConversationOrderedMessages = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelOrderedMessages(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsInputDisabled = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? isConversationModelInputDisabled(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationFetchAdditionalMessagesThrottled = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      if (!conversation) {
        return undefined;
      }
      const fetchAdditionalMessagesThrottledCallback =
        fetchAdditionalMessagesThrottled(conversation);
      return async () => {
        const updatedConversation =
          await fetchAdditionalMessagesThrottledCallback();
        storeUpdatedConversation(updatedConversation);
        return updatedConversation;
      };
    },
    [getConversationByIdentifier, storeUpdatedConversation]
  );

  const getConversationOtherPartyUserType = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelOtherPartyUserType(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationParticipants = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelParticipants(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationShouldShowLoadingSpinner = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation?.isLoading || !!conversation?.nextRequest;
    },
    [getConversationByIdentifier]
  );

  const getConversationOpenConversation = useCallback(
    (conversationIdentifier: string) => {
      if (!setTotalUnreadConversations) {
        return undefined;
      }

      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? async () => {
            const [updatedConversation, totalUnreadConversations] =
              await openConversation(conversation);
            storeUpdatedConversation(updatedConversation);
            setTotalUnreadConversations(totalUnreadConversations);
            return updatedConversation;
          }
        : undefined;
    },
    [
      getConversationByIdentifier,
      setTotalUnreadConversations,
      storeUpdatedConversation,
    ]
  );

  const getConversationMessageAuthors = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)
        ?.messageAuthors;
    },
    [getConversationByIdentifier]
  );

  const hasConversationWithRetailer = useCallback(
    (retailerToken: string) => {
      return !!conversationByRetailerToken?.get(retailerToken);
    },
    [conversationByRetailerToken]
  );

  const hasConversationWithBrand = useCallback(
    (brandToken: string) => {
      return !!conversationByBrandToken?.get(brandToken);
    },
    [conversationByBrandToken]
  );

  return useMemo(
    () => ({
      hasConversation,
      getConversationToken,
      getConversationCreatedAt,
      getConversationLatestMessage,
      getConversationRetailer,
      getConversationBrand,
      getConversationOtherPartyType,
      getConversationOtherPartyUserToken,
      getConversationOtherPartyRetailerToken,
      getConversationOtherPartyBrandToken,
      getConversationIsUnread,
      getConversationOtherPartyFirstName,
      getConversationOtherPartyBusinessImage,
      getConversationIsSupportConversation,
      getConversationIsMarkedAsSpam,
      getConversationIsStarredByBrand,
      getConversationOtherPartyCompanyName,
      getConversationIdentifier,
      getConversationKey,
      getConversationOtherParty,
      getConversationUserToken,
      getConversationOtherPartyProfileImage,
      getConversationOrderedMessages,
      getConversationIsInputDisabled,
      getConversationFetchAdditionalMessagesThrottled,
      getConversationOtherPartyUserType,
      getConversationParticipants,
      getConversationShouldShowLoadingSpinner,
      getConversationOpenConversation,
      getConversationMessageAuthors,
      isConversationToken,
      hasConversationWithRetailer,
      hasConversationWithBrand,
    }),
    [
      getConversationToken,
      getConversationBrand,
      getConversationCreatedAt,
      getConversationFetchAdditionalMessagesThrottled,
      getConversationIdentifier,
      getConversationIsInputDisabled,
      getConversationIsMarkedAsSpam,
      getConversationIsStarredByBrand,
      getConversationIsSupportConversation,
      getConversationIsUnread,
      getConversationKey,
      getConversationLatestMessage,
      getConversationMessageAuthors,
      getConversationOpenConversation,
      getConversationOrderedMessages,
      getConversationOtherParty,
      getConversationOtherPartyBrandToken,
      getConversationOtherPartyBusinessImage,
      getConversationOtherPartyCompanyName,
      getConversationOtherPartyFirstName,
      getConversationOtherPartyProfileImage,
      getConversationOtherPartyRetailerToken,
      getConversationOtherPartyType,
      getConversationOtherPartyUserToken,
      getConversationOtherPartyUserType,
      getConversationParticipants,
      getConversationRetailer,
      getConversationShouldShowLoadingSpinner,
      getConversationUserToken,
      hasConversation,
      hasConversationWithBrand,
      hasConversationWithRetailer,
    ]
  );
};

export const useMessengerStoreLatestUnreadPopUpConversation = () => {
  const { conversationByUserToken } = useMessengerStoreConversationStorage();

  const latestUnreadPopUpConversation = useMemo(() => {
    const conversations: ConversationModel[] = Array.from(
      conversationByUserToken?.values() ?? []
    );
    let latestUnreadPopUpConversation = null;

    for (const conversation of conversations) {
      const latestMessage = getConversationModelLatestMessage(conversation);
      const isFromOtherUser =
        latestMessage &&
        isMessageModelFromOtherUser(latestMessage, conversation);
      if (
        latestMessage &&
        isFromOtherUser &&
        getMessageModelShouldPopUp(latestMessage) &&
        !isMessageModelRead(latestMessage, conversation)
      ) {
        if (!latestUnreadPopUpConversation) {
          latestUnreadPopUpConversation = conversation;
        } else {
          const currentModified =
            getConversationModelLastModified(conversation) || 0;
          const latestModified =
            getConversationModelLastModified(latestUnreadPopUpConversation) ||
            0;
          if (currentModified > latestModified) {
            latestUnreadPopUpConversation = conversation;
          }
        }
      }
    }

    return latestUnreadPopUpConversation;
  }, [conversationByUserToken]);

  const latestUnreadPopUpConversationConversationToken =
    latestUnreadPopUpConversation
      ? getConversationModelToken(latestUnreadPopUpConversation)
      : undefined;

  const {
    getConversationOtherPartyUserToken,
    getConversationOtherPartyBrandToken,
    getConversationOtherPartyRetailerToken,
  } = useMessengerStoreGetConversation();

  const latestUnreadPopUpConversationOtherPartyUserToken =
    latestUnreadPopUpConversationConversationToken
      ? getConversationOtherPartyUserToken(
          latestUnreadPopUpConversationConversationToken
        )
      : undefined;
  const latestUnreadPopUpConversationOtherPartyBrandToken =
    latestUnreadPopUpConversationConversationToken
      ? getConversationOtherPartyBrandToken(
          latestUnreadPopUpConversationConversationToken
        )
      : undefined;
  const latestUnreadPopUpConversationOtherPartyRetailerToken =
    latestUnreadPopUpConversationConversationToken
      ? getConversationOtherPartyRetailerToken(
          latestUnreadPopUpConversationConversationToken
        )
      : undefined;

  const hasUnreadPopUpConversation =
    !!latestUnreadPopUpConversationConversationToken;

  return useMemo(
    () => ({
      latestUnreadPopUpConversationConversationToken,
      latestUnreadPopUpConversationOtherPartyUserToken,
      latestUnreadPopUpConversationOtherPartyBrandToken,
      latestUnreadPopUpConversationOtherPartyRetailerToken,
      hasUnreadPopUpConversation,
    }),
    [
      latestUnreadPopUpConversationConversationToken,
      latestUnreadPopUpConversationOtherPartyUserToken,
      latestUnreadPopUpConversationOtherPartyBrandToken,
      latestUnreadPopUpConversationOtherPartyRetailerToken,
      hasUnreadPopUpConversation,
    ]
  );
};

export const useMessengerStoreCurrentConversation = () => {
  const { currentConversationIdentifier, setCurrentConversationIdentifier } =
    useMessengerContext() ?? {};

  const {
    getConversationKey,
    getConversationToken,
    getConversationOtherPartyUserToken,
    getConversationOtherPartyBrandToken,
    getConversationOtherPartyRetailerToken,
    getConversationOtherParty,
    getConversationUserToken,
    getConversationBrand,
    getConversationRetailer,
    getConversationOtherPartyProfileImage,
    getConversationOtherPartyCompanyName,
    getConversationOtherPartyBusinessImage,
    getConversationLatestMessage,
    getConversationOrderedMessages,
    getConversationIsUnread,
    getConversationIsInputDisabled,
    getConversationIsMarkedAsSpam,
    getConversationIsStarredByBrand,
    getConversationFetchAdditionalMessagesThrottled,
    getConversationOtherPartyUserType,
    getConversationParticipants,
    getConversationOtherPartyFirstName,
    getConversationShouldShowLoadingSpinner,
    getConversationOpenConversation,
    getConversationMessageAuthors,
  } = useMessengerStoreGetConversation();

  const { sendMessageInConversation } =
    useMessengerStoreConversationManagement();

  const { setCurrentConversation } = useMessengerStoreConversationFetching();

  const { hasConversationWithIdentifier } =
    useMessengerStoreConversationIdentification();

  const hasCurrentConversation = !!currentConversationIdentifier;

  // Added helper to get the current conversation from the store
  const { getConversationByIdentifier } =
    useMessengerStoreConversationIdentification();
  const getCurrentConversation = useCallback(() => {
    if (!currentConversationIdentifier) {
      return undefined;
    }
    return getConversationByIdentifier(currentConversationIdentifier);
  }, [currentConversationIdentifier, getConversationByIdentifier]);

  const currentConversationToken = currentConversationIdentifier
    ? getConversationToken(currentConversationIdentifier)
    : undefined;
  const currentConversationKey = currentConversationIdentifier
    ? getConversationKey(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyUserToken = currentConversationIdentifier
    ? getConversationOtherPartyUserToken(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyBrandToken = currentConversationIdentifier
    ? getConversationOtherPartyBrandToken(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyRetailerToken =
    currentConversationIdentifier
      ? getConversationOtherPartyRetailerToken(currentConversationIdentifier)
      : undefined;
  const currentConversationOtherParty = currentConversationIdentifier
    ? getConversationOtherParty(currentConversationIdentifier)
    : undefined;
  const currentConversationUserToken = currentConversationIdentifier
    ? getConversationUserToken(currentConversationIdentifier)
    : undefined;
  const currentConversationBrand = currentConversationIdentifier
    ? getConversationBrand(currentConversationIdentifier)
    : undefined;
  const currentConversationRetailer = currentConversationIdentifier
    ? getConversationRetailer(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyProfileImage =
    currentConversationIdentifier
      ? getConversationOtherPartyProfileImage(currentConversationIdentifier)
      : undefined;
  const currentConversationOtherPartyCompanyName = currentConversationIdentifier
    ? getConversationOtherPartyCompanyName(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyBusinessImage =
    currentConversationIdentifier
      ? getConversationOtherPartyBusinessImage(currentConversationIdentifier)
      : undefined;
  const currentConversationLatestMessage = currentConversationIdentifier
    ? getConversationLatestMessage(currentConversationIdentifier)
    : undefined;
  const currentConversationOrderedMessages = currentConversationIdentifier
    ? getConversationOrderedMessages(currentConversationIdentifier)
    : undefined;
  const currentConversationIsUnread = currentConversationIdentifier
    ? getConversationIsUnread(currentConversationIdentifier)
    : undefined;
  const currentConversationIsInputDisabled = currentConversationIdentifier
    ? getConversationIsInputDisabled(currentConversationIdentifier)
    : undefined;
  const currentConversationIsMarkedAsSpam = currentConversationIdentifier
    ? getConversationIsMarkedAsSpam(currentConversationIdentifier)
    : undefined;
  const currentConversationIsStarredByBrand = currentConversationIdentifier
    ? getConversationIsStarredByBrand(currentConversationIdentifier)
    : undefined;

  const currentConversationFetchAdditionalMessagesThrottled =
    useCallback(() => {
      if (!getConversationFetchAdditionalMessagesThrottled) {
        return undefined;
      }

      if (currentConversationIdentifier) {
        getConversationFetchAdditionalMessagesThrottled(
          currentConversationIdentifier
        )?.();
      }
    }, [
      currentConversationIdentifier,
      getConversationFetchAdditionalMessagesThrottled,
    ]);

  const currentConversationOtherPartyUserType = currentConversationIdentifier
    ? getConversationOtherPartyUserType(currentConversationIdentifier)
    : undefined;
  const currentConversationParticipants = currentConversationIdentifier
    ? getConversationParticipants(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyFirstName = currentConversationIdentifier
    ? getConversationOtherPartyFirstName(currentConversationIdentifier)
    : undefined;
  const currentConversationShouldShowLoadingSpinner =
    currentConversationIdentifier
      ? getConversationShouldShowLoadingSpinner(currentConversationIdentifier)
      : undefined;

  const currentConversationOpenConversation = useCallback(() => {
    if (currentConversationIdentifier) {
      getConversationOpenConversation(currentConversationIdentifier)?.();
    }
  }, [currentConversationIdentifier, getConversationOpenConversation]);

  const currentConversationMessageAuthors = currentConversationIdentifier
    ? getConversationMessageAuthors(currentConversationIdentifier)
    : undefined;

  const sendMessageInCurrentConversation = useCallback(
    async (
      message: Partial<IMessengerMessage>,
      images: readonly File[] | undefined,
      products: string[] = []
    ) => {
      if (!currentConversationIdentifier) {
        return;
      }

      await sendMessageInConversation(
        currentConversationIdentifier,
        message,
        images,
        products
      );
      return Promise.resolve();
    },
    [currentConversationIdentifier, sendMessageInConversation]
  );

  const updateCurrentConversationFromIdentifier = useCallback(
    (conversationIdentifier: string | undefined) => {
      if (!setCurrentConversationIdentifier) {
        return;
      }

      if (!hasConversationWithIdentifier) {
        return;
      }

      if (!conversationIdentifier) {
        setCurrentConversationIdentifier(undefined);
        return;
      }

      if (
        !hasConversationWithIdentifier(conversationIdentifier) &&
        !hasCurrentConversation
      ) {
        return;
      }

      const conversationTokenMatch =
        getConversationToken(conversationIdentifier) ===
        (currentConversationIdentifier
          ? getConversationToken(currentConversationIdentifier)
          : undefined);
      const brandTokenMatch =
        getConversationOtherPartyBrandToken(conversationIdentifier) ===
        (currentConversationIdentifier
          ? getConversationOtherPartyBrandToken(currentConversationIdentifier)
          : undefined);
      const retailerTokenMatch =
        getConversationOtherParty(conversationIdentifier)?.retailer_token ===
        (currentConversationIdentifier
          ? getConversationOtherParty(currentConversationIdentifier)
              ?.retailer_token
          : undefined);

      setCurrentConversationIdentifier(conversationIdentifier);

      if (
        !conversationTokenMatch &&
        (!brandTokenMatch || !retailerTokenMatch)
      ) {
        setCurrentConversation({
          otherPartyUserToken: getConversationUserToken(conversationIdentifier),
          retailerToken: getConversationOtherParty(conversationIdentifier)
            ?.retailer_token,
          brandToken: getConversationOtherPartyBrandToken(
            conversationIdentifier
          ),
          conversationToken: getConversationToken(conversationIdentifier),
        });
      }
    },
    [
      hasConversationWithIdentifier,
      hasCurrentConversation,
      getConversationToken,
      currentConversationIdentifier,
      getConversationOtherPartyBrandToken,
      getConversationOtherParty,
      setCurrentConversationIdentifier,
      setCurrentConversation,
      getConversationUserToken,
    ]
  );

  return useMemo(
    () => ({
      currentConversationIdentifier,
      currentConversationKey,
      currentConversationToken,
      currentConversationOtherPartyUserToken,
      currentConversationOtherPartyBrandToken,
      currentConversationOtherPartyRetailerToken,
      currentConversationOtherParty,
      currentConversationUserToken,
      currentConversationBrand,
      currentConversationRetailer,
      currentConversationOtherPartyProfileImage,
      currentConversationOtherPartyCompanyName,
      currentConversationOtherPartyBusinessImage,
      currentConversationLatestMessage,
      currentConversationOrderedMessages,
      currentConversationIsUnread,
      currentConversationIsInputDisabled,
      currentConversationIsMarkedAsSpam,
      currentConversationIsStarredByBrand,
      currentConversationFetchAdditionalMessagesThrottled,
      currentConversationOtherPartyUserType,
      currentConversationParticipants,
      currentConversationOtherPartyFirstName,
      currentConversationShouldShowLoadingSpinner,
      currentConversationOpenConversation,
      currentConversationMessageAuthors,
      hasCurrentConversation,
      setCurrentConversation,
      updateCurrentConversationFromIdentifier,
      sendMessageInCurrentConversation,
      getCurrentConversation,
    }),
    [
      currentConversationBrand,
      currentConversationFetchAdditionalMessagesThrottled,
      currentConversationIdentifier,
      currentConversationIsInputDisabled,
      currentConversationIsMarkedAsSpam,
      currentConversationIsStarredByBrand,
      currentConversationIsUnread,
      currentConversationKey,
      currentConversationLatestMessage,
      currentConversationMessageAuthors,
      currentConversationOpenConversation,
      currentConversationOrderedMessages,
      currentConversationOtherParty,
      currentConversationOtherPartyBrandToken,
      currentConversationOtherPartyBusinessImage,
      currentConversationOtherPartyCompanyName,
      currentConversationOtherPartyFirstName,
      currentConversationOtherPartyProfileImage,
      currentConversationOtherPartyRetailerToken,
      currentConversationOtherPartyUserToken,
      currentConversationOtherPartyUserType,
      currentConversationParticipants,
      currentConversationRetailer,
      currentConversationShouldShowLoadingSpinner,
      currentConversationToken,
      currentConversationUserToken,
      hasCurrentConversation,
      sendMessageInCurrentConversation,
      setCurrentConversation,
      updateCurrentConversationFromIdentifier,
      getCurrentConversation,
    ]
  );
};

export const useMessengerStoreGetConversationOrUndefined = () => {
  const { conversationByBrandToken } = useMessengerStoreConversationStorage();

  const hasConversationWithBrand = useCallback(
    (brandToken: string) => {
      return !!conversationByBrandToken?.get(brandToken);
    },
    [conversationByBrandToken]
  );

  return { hasConversationWithBrand };
};

export const useMessengerStoreConversationMessage = () => {
  const { strictLocalize } = useStrictLocalization();
  const { brand } = useMessengerContext() ?? {};
  const { getConversationByIdentifier } =
    useMessengerStoreConversationIdentification();

  const isMessageFromCurrentUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelFromCurrentUser(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const isMessageFromOtherUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelFromOtherUser(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const isMessageFromSameRetailerAsCurrentUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelFromSameRetailerAsCurrentUser(
        message,
        conversation,
        conversation.messageAuthors
      );
    },
    [getConversationByIdentifier]
  );

  const isMessageFromSameBrandAsCurrentUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelFromSameBrandAsCurrentUser(
        message,
        conversation,
        conversation.messageAuthors
      );
    },
    [getConversationByIdentifier]
  );

  const isMessageRead = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelRead(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const isMessageLatestReadFromCurrentUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelLatestReadFromCurrentUser(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const isMessageLatestDelivered = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelLatestDelivered(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const sendMessage = useCallback(
    (
      message: MessageModel,
      conversationIdentifier: string
    ): Promise<unknown> => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return Promise.resolve();
      }
      return sendMessageModel(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const deleteMessage = useCallback(
    (message: MessageModel, conversationIdentifier: string): Promise<void> => {
      if (!getConversationByIdentifier) {
        return Promise.resolve();
      }

      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return Promise.resolve();
      }
      return (
        deleteMessageModel(
          conversation,
          getMessageModelToken(message),
          strictLocalize
        )
          // Cast to `Promise<void>` to satisfy the type checker
          .then(() => {})
      );
    },
    [getConversationByIdentifier, strictLocalize]
  );

  const getOtherPartyProfilePicUrl = useCallback(
    (
      message: MessageModel,
      conversationIdentifier: string
    ): string | undefined => {
      if (!getConversationByIdentifier) {
        return undefined;
      }

      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }
      return getMessageModelOtherPartyProfilePicUrl(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const getCurrentPartyProfilePicUrl = useCallback(
    (
      _message: MessageModel,
      conversationIdentifier: string
    ): string | undefined => {
      if (!getConversationByIdentifier) {
        return undefined;
      }

      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }
      return getMessageModelCurrentPartyProfilePicUrl(conversation, brand);
    },
    [brand, getConversationByIdentifier]
  );

  const getMessageOtherPartyFirstName = useCallback(
    (
      message: MessageModel,
      conversationIdentifier: string
    ): string | undefined => {
      if (!getConversationByIdentifier) {
        return undefined;
      }

      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }
      return getMessageModelOtherPartyFirstName(message, conversation);
    },
    [getConversationByIdentifier]
  );

  return useMemo(
    () => ({
      isMessageFromCurrentUser,
      isMessageFromOtherUser,
      isMessageFromSameRetailerAsCurrentUser,
      isMessageFromSameBrandAsCurrentUser,
      isMessageRead,
      isMessageLatestReadFromCurrentUser,
      isMessageLatestDelivered,
      sendMessage,
      deleteMessage,
      getOtherPartyProfilePicUrl,
      getCurrentPartyProfilePicUrl,
      getMessageOtherPartyFirstName,
    }),
    [
      isMessageFromCurrentUser,
      isMessageFromOtherUser,
      isMessageFromSameRetailerAsCurrentUser,
      isMessageFromSameBrandAsCurrentUser,
      isMessageRead,
      isMessageLatestReadFromCurrentUser,
      isMessageLatestDelivered,
      sendMessage,
      deleteMessage,
      getOtherPartyProfilePicUrl,
      getCurrentPartyProfilePicUrl,
      getMessageOtherPartyFirstName,
    ]
  );
};

/**
 * Hooks used internally for fetching conversations. Note these are referenced by
 * hooks that are brought in on the visitor pages so *all* hooks here must at least
 * support being *defined* in the absence of a MessengerStore.
 */
const useMessengerStoreConversationFetching = () => {
  const { strictLocalize } = useStrictLocalization();
  const { storeUpdatedConversation } =
    useMessengerStoreStoreUpdatedConversation();
  const { getConversationByIdentifier } =
    useMessengerStoreConversationIdentification();
  const {
    conversationByConversationToken,
    conversationByBrandToken,
    conversationByRetailerToken,
    conversationByUserToken,
  } = useMessengerStoreConversationStorage();
  const { mainStreamFetchState } = useMessengerStoreMainStreamFetchState();
  const { updateConversationInFetchState, existsInFetchState } =
    useMessengerStoreStoreUpdatedConversation();

  const {
    setAllMainStreamFetchState,
    setUnreadOnlyMainStreamFetchState,
    setStarredByBrandOnlyMainStreamFetchState,
    user,
    latestMessageUpdatedAt,
    setLatestMessageUpdatedAt,
    setSearchResults,
    incrementIsLoadingCount,
    decrementIsLoadingCount,
    brandContext,
    currentConversationIdentifier,
    setCurrentConversationIdentifier,
    query,
    setTotalUnreadConversations,
    setTotalStarredByBrandConversations,
    retailerContext,
    showOnlyUnreadConversations,
    showOnlyStarredByBrandConversations,
  } = useMessengerContext() ?? {};

  const userFromStore = user;
  const createOrUpdateConversation = useCallback(
    (
      conversation: IMessengerConversation,
      simple_brand?: IGetMessengerConversationsResponse.ISimpleBrand,
      simple_retailer?: IGetMessengerConversationsResponse.ISimpleRetailer,
      userArg?: IUser
    ) => {
      const user = userArg ?? userFromStore;
      if (
        !user ||
        !setAllMainStreamFetchState ||
        !setUnreadOnlyMainStreamFetchState ||
        !setStarredByBrandOnlyMainStreamFetchState ||
        !setLatestMessageUpdatedAt
      ) {
        return;
      }

      const otherUser = conversation.other_party;
      const otherIsBrandUser =
        otherUser && otherUser.type === IUser.Type.BRAND_USER;
      const otherIsRetailerUser =
        otherUser && otherUser.type === IUser.Type.USER;
      const otherPartyUserToken =
        conversation.other_party && conversation.other_party.token;

      // Multi-brand allows users to be linked to multiple brands and switch between them.
      // As a result, a WebSocket channel may have been initiated and connected by a different brand.
      // Therefore, when receiving a message from the WebSocket, we need to verify if the message is intended for the current brand.
      // More details in https://www.notion.so/faire/Test-Spec-Multi-Brand-1272efb5c25a80bd853dcc6bf66f9791?pvs=4#1512efb5c25a80299108eb4fd5453f5b
      const currentBrandToken = getBrandTokenInSession();
      const isMessageForAnotherBrand =
        otherIsRetailerUser && conversation.brand_token !== currentBrandToken;

      if (!otherUser || !otherPartyUserToken || isMessageForAnotherBrand) {
        return;
      }

      // Note: We MUST use a functional update here to ensure that multiple
      // updates in the same render cycle get accumulated, as the
      // `updateConversationModel` function takes the union of messages and
      // messages may be dropped if the most recent conversation model simply
      // overwrites the previous one.
      let updatedConversation: ConversationModel | undefined = undefined;
      setAllMainStreamFetchState((mainStreamFetchState) => {
        let existingConversation: ConversationModel | undefined;
        if (conversation.token) {
          existingConversation =
            mainStreamFetchState.conversationByConversationToken.get(
              conversation.token
            );
        } else if (otherIsBrandUser && conversation.brand_token) {
          existingConversation =
            mainStreamFetchState.conversationByBrandToken.get(
              conversation.brand_token
            );
        } else if (otherIsRetailerUser && otherUser.retailer_token) {
          existingConversation =
            mainStreamFetchState.conversationByRetailerToken.get(
              otherUser.retailer_token
            );
        }

        // Create or update the conversation
        if (existingConversation) {
          updatedConversation = updateConversationModel(
            existingConversation,
            conversation,
            simple_brand,
            simple_retailer
          );
        } else {
          updatedConversation = createConversationModel(
            conversation,
            strictLocalize,
            user,
            simple_brand,
            simple_retailer
          );
        }

        updatedConversation = createSyntheticStatusMessage(
          updatedConversation,
          updatedConversation.data?.other_party_status
        );

        // Create a new state object with the updated conversation
        return updateConversationInFetchState(
          mainStreamFetchState,
          updatedConversation
        );
      });

      // However, we don't have to re-apply the changes for the other
      // fetch states, as the previous conversation is guaranteed to be the
      // same, so we just have to stick in the new conversation.
      if (updatedConversation) {
        // Using a type annotation here is safer than using `as` or `!`.
        const updatedConversationTypecast: ConversationModel =
          updatedConversation;
        setUnreadOnlyMainStreamFetchState((unreadOnlyMainStreamFetchState) => {
          if (
            isConversationModelUnRead(updatedConversationTypecast) ||
            existsInFetchState(
              unreadOnlyMainStreamFetchState,
              updatedConversationTypecast
            )
          ) {
            return updateConversationInFetchState(
              unreadOnlyMainStreamFetchState,
              updatedConversationTypecast
            );
          }
          return unreadOnlyMainStreamFetchState;
        });

        setStarredByBrandOnlyMainStreamFetchState(
          (starredByBrandOnlyMainStreamFetchState) => {
            if (
              isConversationModelStarredByBrand(updatedConversationTypecast) ||
              existsInFetchState(
                starredByBrandOnlyMainStreamFetchState,
                updatedConversationTypecast
              )
            ) {
              return updateConversationInFetchState(
                starredByBrandOnlyMainStreamFetchState,
                updatedConversationTypecast
              );
            }
            return starredByBrandOnlyMainStreamFetchState;
          }
        );
      }

      // Track conversation messages as needed
      if (
        conversation.latest_message?.author_token === otherUser.token &&
        otherIsBrandUser &&
        latestMessageUpdatedAt !== conversation.latest_message?.updated_at &&
        conversation.latest_message?.text !== "review request"
      ) {
        trackMessengerMessageFromBrandToRetailer(
          conversation?.retailer_token ?? "",
          conversation.brand_token ?? "",
          conversation.latest_message?.updated_at?.toString() ?? "",
          conversation.latest_message?.text ?? ""
        );
        setLatestMessageUpdatedAt(conversation.latest_message?.updated_at);
      }

      if (
        conversation.latest_message?.author_token === otherUser.token &&
        otherIsRetailerUser &&
        latestMessageUpdatedAt !== conversation.latest_message?.updated_at &&
        conversation.latest_message?.text !== "review request"
      ) {
        trackMessengerMessageFromRetailerToBrand(
          "",
          conversation.brand_token ?? "",
          conversation?.retailer_token ?? "",
          conversation.latest_message?.updated_at?.toString() ?? "",
          conversation.latest_message?.text ?? "",
          ""
        );
      }
    },
    [
      userFromStore,
      setAllMainStreamFetchState,
      setUnreadOnlyMainStreamFetchState,
      setStarredByBrandOnlyMainStreamFetchState,
      setLatestMessageUpdatedAt,
      latestMessageUpdatedAt,
      updateConversationInFetchState,
      strictLocalize,
      existsInFetchState,
    ]
  );

  const createOrUpdateConversations = useCallback(
    (
      conversations: IMessengerConversation[],
      simple_brands: Partial<
        Record<string, IGetMessengerConversationsResponse.ISimpleBrand>
      >,
      simple_retailers: Partial<
        Record<string, IGetMessengerConversationsResponse.ISimpleRetailer>
      >,
      userArg?: IUser
    ) => {
      const user = userArg ?? userFromStore;
      if (!user) {
        return;
      }

      conversations.forEach((conversation) => {
        if (!conversation.other_party) {
          throw new Error("An unknown error occurred");
        }
        const brand = simple_brands[conversation.brand_token!];
        const retailer = simple_retailers[conversation.retailer_token!];

        createOrUpdateConversation(conversation, brand, retailer, user);
      });
    },
    [createOrUpdateConversation, userFromStore]
  );

  const fetchConversationByToken = useCallback(
    async (
      conversationToken?: string,
      retailerToken?: string,
      brandToken?: string
    ): Promise<void> => {
      if (
        !user ||
        !incrementIsLoadingCount ||
        !setSearchResults ||
        !decrementIsLoadingCount
      ) {
        return;
      }

      try {
        incrementIsLoadingCount();

        let response: IGetConversationResponseV3;

        if (conversationToken) {
          response = await fetchMessengerConversation(conversationToken);
        } else if (user?.type === IUser.Type.BRAND_USER && retailerToken) {
          response = await fetchMessengerConversationByRetailer(retailerToken);
        } else if (user?.type === IUser.Type.USER && brandToken) {
          response = await fetchMessengerConversationByBrand(brandToken);
        } else {
          logError(
            new Error(
              "This function should recieve a conversation_token, or appropriate [brand | retailer]_token relative to the user"
            )
          );
          return;
        }

        const { conversation, simple_brand, simple_retailer } = response;

        if (!conversation || (!simple_retailer && !simple_brand)) {
          logError(
            new Error(
              "This request should return a conversation and information about the retailer or the brand"
            )
          );
          return;
        }

        if (!query && !brandContext) {
          setSearchResults(undefined);
        }

        if (conversation) {
          createOrUpdateConversation(
            conversation,
            simple_brand,
            simple_retailer
          );
        }
      } catch (e) {
        logError(e);
      } finally {
        decrementIsLoadingCount();
      }
    },
    [
      user,
      incrementIsLoadingCount,
      query,
      brandContext,
      setSearchResults,
      createOrUpdateConversation,
      decrementIsLoadingCount,
    ]
  );

  const fetchConversations = useCallback(
    async (
      data: Partial<IGetMessengerConversationsRequest> = {},
      isMainStreamFetch?: boolean,
      isSearch?: boolean,
      userArg?: IUser
    ): Promise<void> => {
      const user = userArg ?? userFromStore;
      if (
        !mainStreamFetchState ||
        !createOrUpdateConversations ||
        !setAllMainStreamFetchState ||
        !setUnreadOnlyMainStreamFetchState ||
        !setStarredByBrandOnlyMainStreamFetchState ||
        !setSearchResults ||
        !setTotalUnreadConversations ||
        !setTotalStarredByBrandConversations
      ) {
        return;
      }

      try {
        const {
          conversations,
          total_unread_conversations,
          total_starred_by_brand_conversations,
          simple_brands = {},
          simple_retailers = {},
          next_request,
        } = await findMessengerConversations(
          IGetMessengerConversationsRequest.build({
            ...data,
            context_token:
              data.context_token ??
              brandContext?.token ??
              retailerContext?.token,
            only_unread:
              data.only_unread || showOnlyUnreadConversations || undefined,
            filter: showOnlyStarredByBrandConversations
              ? IGetMessengerConversationsRequest.Filter.STARRED_BY_BRAND
              : undefined,
          })
        );

        if (!conversations) {
          return;
        }

        if (isSearch) {
          setSearchResults(
            compact(
              conversations.map(
                (conversation) =>
                  conversation.other_party && conversation.other_party.token
              )
            ).reduce((set, token) => set.add(token), new Set<string>())
          );
        } else if (!query && !brandContext) {
          setSearchResults(undefined);
        }

        if (isMainStreamFetch) {
          const conversationTokens = conversations.map(
            (conversation) => conversation.token
          );
          if (
            isEqual(
              mainStreamFetchState.lastMainStreamConversationRequestTokens,
              conversationTokens
            )
          ) {
            // If we received no new conversation tokens, it implies there's no
            // next request.
            switch (true) {
              case showOnlyUnreadConversations:
                setUnreadOnlyMainStreamFetchState((state) =>
                  setNextRequest(state, undefined)
                );
                break;
              case showOnlyStarredByBrandConversations:
                setStarredByBrandOnlyMainStreamFetchState((state) =>
                  setNextRequest(state, undefined)
                );
                break;
              default:
                setAllMainStreamFetchState((state) =>
                  setNextRequest(state, undefined)
                );
            }
            return;
          }

          // Update mainStreamFetchState with tokens and nextRequest
          switch (true) {
            case showOnlyUnreadConversations:
              setUnreadOnlyMainStreamFetchState((state) => {
                const updatedState = setLastMainStreamConversationRequestTokens(
                  state,
                  conversationTokens
                );
                return setNextRequest(updatedState, next_request);
              });
              break;
            case showOnlyStarredByBrandConversations:
              setStarredByBrandOnlyMainStreamFetchState((state) => {
                const updatedState = setLastMainStreamConversationRequestTokens(
                  state,
                  conversationTokens
                );
                return setNextRequest(updatedState, next_request);
              });
              break;
            default:
              setAllMainStreamFetchState((state) => {
                const updatedState = setLastMainStreamConversationRequestTokens(
                  state,
                  conversationTokens
                );
                return setNextRequest(updatedState, next_request);
              });
          }

          setTotalUnreadConversations(total_unread_conversations);
          setTotalStarredByBrandConversations(
            total_starred_by_brand_conversations
          );
        }

        if (brandContext && !simple_brands[brandContext.token!]) {
          simple_brands[brandContext.token!] = brandContext;
        }

        createOrUpdateConversations(
          conversations,
          simple_brands,
          simple_retailers,
          user
        );
      } catch (e) {
        logError(e);
      }
    },
    [
      userFromStore,
      mainStreamFetchState,
      createOrUpdateConversations,
      setAllMainStreamFetchState,
      setUnreadOnlyMainStreamFetchState,
      setStarredByBrandOnlyMainStreamFetchState,
      setSearchResults,
      setTotalUnreadConversations,
      setTotalStarredByBrandConversations,
      brandContext,
      retailerContext?.token,
      showOnlyUnreadConversations,
      showOnlyStarredByBrandConversations,
      query,
    ]
  );

  /*
   * Async
   *
   * Keeping persistent data that doesn't have an ordering with unlimited scroll is very difficult.
   * - needs to be persistent because
   *   - we don't know if the user is looking at it (scroll)
   * - order of conversation is constantly changing based on which ones have the newest messages
   */

  /**
   * When messenger mounts for the first time this populate conversations and nextRequest for scroll
   * Future calls makes sure all conversations are up to date
   *
   * TODO: should this be debounced? first call does something but subsequent calls do nothing until 200ms later?
   */
  const firstFetchOrUpdateConversations = useCallback(
    async (userArg?: IUser) => {
      const user = userArg ?? userFromStore;
      if (
        !user ||
        !mainStreamFetchState ||
        !setUnreadOnlyMainStreamFetchState ||
        !setStarredByBrandOnlyMainStreamFetchState ||
        !setAllMainStreamFetchState
      ) {
        return;
      }

      if (mainStreamFetchState.isFirstFetch) {
        // Update the isFirstFetch flag
        switch (true) {
          case showOnlyUnreadConversations:
            setUnreadOnlyMainStreamFetchState((state) =>
              setIsFirstFetch(state, false)
            );
            break;
          case showOnlyStarredByBrandConversations:
            setStarredByBrandOnlyMainStreamFetchState((state) =>
              setIsFirstFetch(state, false)
            );
            break;
          default:
            setAllMainStreamFetchState((state) =>
              setIsFirstFetch(state, false)
            );
        }
        return fetchConversations(undefined, true, false, user);
      } else {
        // TODO: keep fetching conversations until we find an unchanged conversation
        //   currently this fails if more than 1 page of conversations has changed
        //   requires refactoring fetchConversations and update conversation to return data about what happens
        return fetchConversations(undefined, false, false, user);
      }
    },
    [
      userFromStore,
      mainStreamFetchState,
      setUnreadOnlyMainStreamFetchState,
      setStarredByBrandOnlyMainStreamFetchState,
      setAllMainStreamFetchState,
      fetchConversations,
      showOnlyUnreadConversations,
      showOnlyStarredByBrandConversations,
    ]
  );

  const nextConversations = useCallback(async () => {
    if (!mainStreamFetchState) {
      return;
    }
    if (mainStreamFetchState.nextRequest) {
      return fetchConversations(mainStreamFetchState.nextRequest, true);
    }
  }, [fetchConversations, mainStreamFetchState]);

  const searchConversations = useCallback(
    async (
      params: Pick<IGetMessengerConversationsRequest, "context_token" | "query">
    ) => {
      await fetchConversations(params, false, true);
    },
    [fetchConversations]
  );

  const firstFetchOrUpdateMessagesInCurrentConversation =
    useCallback(async () => {
      if (!user) {
        return;
      }
      if (!currentConversationIdentifier) {
        return;
      }
      const currentConversation = getConversationByIdentifier(
        currentConversationIdentifier
      );
      if (!currentConversation) {
        return;
      }
      storeUpdatedConversation(
        await firstFetchOrUpdateMessages(currentConversation)
      );
    }, [
      currentConversationIdentifier,
      getConversationByIdentifier,
      storeUpdatedConversation,
      user,
    ]);

  const attemptToFindAndSetConversation = useCallback(
    (
      conversationToken?: string,
      brandToken?: string,
      retailerToken?: string,
      otherPartyUserToken?: string
    ): boolean => {
      if (
        !user ||
        !conversationByConversationToken ||
        !setCurrentConversationIdentifier
      ) {
        return false;
      }

      let conversation: ConversationModel | undefined;

      if (
        conversationToken &&
        conversationByConversationToken?.get(conversationToken)
      ) {
        conversation = conversationByConversationToken?.get(conversationToken);
      } else {
        // Since we're only getting brand or retailer tokens, we have to use them to do the initial look-up
        if (brandToken && conversationByBrandToken?.get(brandToken)) {
          conversation = conversationByBrandToken?.get(brandToken);
        } else if (
          retailerToken &&
          conversationByRetailerToken?.get(retailerToken)
        ) {
          conversation = conversationByRetailerToken?.get(retailerToken);
        } else if (
          otherPartyUserToken &&
          conversationByUserToken?.get(otherPartyUserToken)
        ) {
          conversation = conversationByUserToken?.get(otherPartyUserToken);
        }

        // If we found a real conversation, let's pull it from the conversation_token indexed list for consistency
        if (
          conversation &&
          getConversationModelToken(conversation) &&
          conversationByConversationToken?.get(
            getConversationModelToken(conversation)
          )
        ) {
          conversation = conversationByConversationToken?.get(
            getConversationModelToken(conversation)
          );
        }
      }

      if (conversation) {
        setCurrentConversationIdentifier(
          getConversationModelIdentifier(conversation)
        );
        return true;
      }
      return false;
    },
    [
      conversationByBrandToken,
      conversationByConversationToken,
      conversationByRetailerToken,
      conversationByUserToken,
      setCurrentConversationIdentifier,
      user,
    ]
  );

  const setCurrentConversation = useCallback(
    async (
      param: {
        otherPartyUserToken?: string;
        brandToken?: string;
        retailerToken?: string;
        conversationToken?: string;
      } = {}
    ): Promise<void> => {
      if (!user || !setCurrentConversationIdentifier) {
        return;
      }

      const {
        otherPartyUserToken,
        brandToken,
        retailerToken,
        conversationToken,
      } = param;

      if (
        !conversationToken &&
        !otherPartyUserToken &&
        !brandToken &&
        !retailerToken
      ) {
        setCurrentConversationIdentifier(undefined);
        return;
      }

      if (
        attemptToFindAndSetConversation(
          conversationToken,
          brandToken,
          retailerToken,
          otherPartyUserToken
        )
      ) {
        return;
      }
      await fetchConversationByToken(
        conversationToken,
        retailerToken,
        brandToken
      );
      if (
        !attemptToFindAndSetConversation(
          conversationToken,
          brandToken,
          retailerToken,
          otherPartyUserToken
        )
      ) {
        throw new Error(
          "A conversation should have been returned by the server."
        );
      }
    },
    [
      user,
      attemptToFindAndSetConversation,
      fetchConversationByToken,
      setCurrentConversationIdentifier,
    ]
  );

  const hasConversationsNextRequest = !!mainStreamFetchState?.nextRequest;

  return useMemo(
    () => ({
      createOrUpdateConversation,
      nextConversations,
      searchConversations,
      fetchConversations,
      firstFetchOrUpdateConversations,
      firstFetchOrUpdateMessagesInCurrentConversation,
      setCurrentConversation,
      hasConversationsNextRequest,
    }),
    [
      createOrUpdateConversation,
      nextConversations,
      searchConversations,
      fetchConversations,
      firstFetchOrUpdateConversations,
      firstFetchOrUpdateMessagesInCurrentConversation,
      setCurrentConversation,
      hasConversationsNextRequest,
    ]
  );
};

export const useMessengerStoreIsLoadingConversations = () => {
  const { isLoadingCount = 0 } = useMessengerContext() ?? {};
  return useMemo(
    () => ({
      isLoadingConversations: isLoadingCount > 0,
    }),
    [isLoadingCount]
  );
};

const useMessengerStoreOrderedConversations = () => {
  const { searchResults, hiddenConversations } = useMessengerContext() ?? {};
  const { mainStreamFetchState } = useMessengerStoreMainStreamFetchState();
  const { allOrderedConversations } =
    useMessengerStoreAllOrderedConversations();

  const orderedConversations = useMemo(() => {
    if (!mainStreamFetchState) {
      return [];
    }
    // TEST(Ian): search results are used when search or context token exists
    let _orderedConversations = allOrderedConversations;
    if (searchResults) {
      _orderedConversations = compact(
        Array.from(searchResults.values()).map((token) =>
          mainStreamFetchState.conversationByUserToken.get(token)
        )
      );
    }

    //filtering out support conversations as well as the staging Olivia conversation that causes issues
    _orderedConversations = FILTER_SUPPORT_CONVERSATION
      ? _orderedConversations.filter(
          (item) =>
            !isConversationModelSupportConversation(item) &&
            item.otherParty?.token !== "u_eafa228a"
        )
      : _orderedConversations;

    // Filtering out conversations hidden by user when they mark it as spam
    _orderedConversations = _orderedConversations.filter(
      (conversation) =>
        !hiddenConversations?.includes(getConversationModelToken(conversation))
    );
    return _orderedConversations;
  }, [
    hiddenConversations,
    allOrderedConversations,
    mainStreamFetchState,
    searchResults,
  ]);

  return useMemo(() => ({ orderedConversations }), [orderedConversations]);
};

export const useMessengerStoreAllOrderedConversations = () => {
  const { retailerContext, brandContext } = useMessengerContext() ?? {};
  const { mainStreamFetchState } = useMessengerStoreMainStreamFetchState();

  const allOrderedConversations = useMemo(() => {
    if (!mainStreamFetchState) {
      return [];
    }
    return uniqBy(
      compact(
        Array.from(mainStreamFetchState.conversationByUserToken?.values() ?? [])
      ),
      (conversation: ConversationModel) =>
        getConversationModelIdentifier(conversation)
    ).sort(
      (conversationA: ConversationModel, conversationB: ConversationModel) => {
        // support conversation comes first
        if (
          isConversationModelSupportConversation(conversationA) !==
          isConversationModelSupportConversation(conversationB)
        ) {
          return isConversationModelSupportConversation(conversationA) ? -1 : 1;
        }

        const retailerContextComparator = conversationRetailerContextComparator(
          conversationA,
          conversationB,
          retailerContext?.owner_user_token
        );

        if (retailerContextComparator !== 0) {
          return retailerContextComparator;
        }

        const brandContextComparator = conversationBrandContextComparator(
          conversationA,
          conversationB,
          brandContext?.token
        );
        if (brandContextComparator !== 0) {
          return brandContextComparator;
        }

        return conversationLastModifiedComparator(conversationA, conversationB);
      }
    );
  }, [
    brandContext?.token,
    mainStreamFetchState,
    retailerContext?.owner_user_token,
  ]);

  return { allOrderedConversations };
};

export const useMessengerStoreHasConversations = () => {
  const { mainStreamFetchState } = useMessengerStoreMainStreamFetchState();
  const hasConversations = useMemo(
    () => (mainStreamFetchState?.conversationByUserToken.size ?? 0) > 0,
    [mainStreamFetchState?.conversationByUserToken.size]
  );
  return { hasConversations };
};

export const useMessengerStoreGetConversationByTypes = () => {
  const { mainStreamFetchState } = useMessengerStoreMainStreamFetchState();

  const getConversationByUser = useCallback(
    (userToken: string): ConversationModel | undefined =>
      mainStreamFetchState?.conversationByUserToken.get(userToken),
    [mainStreamFetchState]
  );

  const getConversationByBrand = useCallback(
    (brandToken: string): ConversationModel | undefined =>
      mainStreamFetchState?.conversationByBrandToken.get(brandToken),
    [mainStreamFetchState]
  );

  const getConversationByRetailer = useCallback(
    (retailerToken: string): ConversationModel | undefined =>
      mainStreamFetchState?.conversationByRetailerToken.get(retailerToken),
    [mainStreamFetchState]
  );

  const getConversationByConversationToken = useCallback(
    (conversationToken: string): ConversationModel | undefined =>
      mainStreamFetchState?.conversationByConversationToken.get(
        conversationToken
      ),
    [mainStreamFetchState]
  );

  return useMemo(
    () => ({
      getConversationByUser,
      getConversationByBrand,
      getConversationByRetailer,
      getConversationByConversationToken,
    }),
    [
      getConversationByUser,
      getConversationByBrand,
      getConversationByRetailer,
      getConversationByConversationToken,
    ]
  );
};

export const useMessengerStoreConversationIdentification = () => {
  const { mainStreamFetchState } = useMessengerStoreMainStreamFetchState();
  const { user } = useMessengerContext() ?? {};

  const getConversationByIdentifier = useCallback(
    (
      conversationIdentifier: string | undefined
    ): ConversationModel | undefined => {
      if (!user || !mainStreamFetchState) {
        return;
      }

      if (!conversationIdentifier) {
        return undefined;
      }

      if (isConversationToken(conversationIdentifier)) {
        return mainStreamFetchState.conversationByConversationToken.get(
          conversationIdentifier
        );
      }

      // Support for deprecated v2 email links that do not pass in brand/retailer tokens
      const conversation =
        user?.type === IUser.Type.BRAND_USER
          ? mainStreamFetchState.conversationByRetailerToken.get(
              conversationIdentifier
            )
          : mainStreamFetchState.conversationByBrandToken.get(
              conversationIdentifier
            );
      if (conversation) {
        return conversation;
      }

      return mainStreamFetchState.conversationByUserToken.get(
        conversationIdentifier
      );
    },
    [user, mainStreamFetchState]
  );

  const hasConversationWithIdentifier = useCallback(
    (identifier: string): boolean => {
      return getConversationByIdentifier(identifier) !== undefined;
    },
    [getConversationByIdentifier]
  );

  return useMemo(
    () => ({
      getConversationByIdentifier,
      hasConversationWithIdentifier,
    }),
    [getConversationByIdentifier, hasConversationWithIdentifier]
  );
};

const useMessengerStoreConversationStorage = () => {
  const { mainStreamFetchState } = useMessengerStoreMainStreamFetchState();

  const conversationByUserToken = mainStreamFetchState?.conversationByUserToken;
  const conversationByBrandToken =
    mainStreamFetchState?.conversationByBrandToken;
  const conversationByRetailerToken =
    mainStreamFetchState?.conversationByRetailerToken;
  const conversationByConversationToken =
    mainStreamFetchState?.conversationByConversationToken;

  return useMemo(
    () => ({
      conversationByUserToken,
      conversationByBrandToken,
      conversationByRetailerToken,
      conversationByConversationToken,
    }),
    [
      conversationByBrandToken,
      conversationByConversationToken,
      conversationByRetailerToken,
      conversationByUserToken,
    ]
  );
};

export const useMessengerStoreStoreUpdatedConversation = () => {
  const {
    setAllMainStreamFetchState,
    setUnreadOnlyMainStreamFetchState,
    setStarredByBrandOnlyMainStreamFetchState,
  } = useMessengerContext() ?? {};

  const existsInFetchState = useCallback(
    (
      fetchState: MainStreamFetchState,
      conversation: ConversationModel
    ): boolean => {
      const conversationToken = getConversationModelToken(conversation);
      const otherPartyUserToken = conversation.otherParty?.token;

      return (
        (!!conversationToken &&
          fetchState.conversationByConversationToken.has(conversationToken)) ||
        (!!otherPartyUserToken &&
          fetchState.conversationByUserToken.has(otherPartyUserToken))
      );
    },
    []
  );

  const updateConversationInFetchState = useCallback(
    (
      fetchState: MainStreamFetchState,
      conversation: ConversationModel
    ): MainStreamFetchState => {
      const conversationToken = getConversationModelToken(conversation);
      const otherPartyUserToken = conversation.otherParty?.token;
      const brandToken = conversation.brand?.token;
      const retailerToken = conversation.otherParty?.retailer_token;
      const isOtherPartyBrandUser =
        isConversationModelOtherPartyBrandUser(conversation);
      const isOtherPartyRetailerUser =
        conversation.otherParty?.type === IUser.Type.USER;

      let newFetchState = fetchState;
      if (conversationToken) {
        if (
          !isEqual(
            newFetchState.conversationByConversationToken.get(
              conversationToken
            ),
            conversation
          )
        ) {
          const newConversationByConversationToken = new Map(
            newFetchState.conversationByConversationToken
          );
          newConversationByConversationToken.set(
            conversationToken,
            conversation
          );
          newFetchState = {
            ...newFetchState,
            conversationByConversationToken: newConversationByConversationToken,
          };
        }
      }

      if (otherPartyUserToken) {
        if (
          !isEqual(
            newFetchState.conversationByUserToken.get(otherPartyUserToken),
            conversation
          )
        ) {
          const newConversationByUserToken = new Map(
            newFetchState.conversationByUserToken
          );
          newConversationByUserToken.set(otherPartyUserToken, conversation);
          newFetchState = {
            ...newFetchState,
            conversationByUserToken: newConversationByUserToken,
          };
        }
      }

      if (isOtherPartyBrandUser && brandToken) {
        if (
          !isEqual(
            newFetchState.conversationByBrandToken.get(brandToken),
            conversation
          )
        ) {
          const newConversationByBrandToken = new Map(
            newFetchState.conversationByBrandToken
          );
          newConversationByBrandToken.set(brandToken, conversation);
          newFetchState = {
            ...newFetchState,
            conversationByBrandToken: newConversationByBrandToken,
          };
        }
      }

      if (isOtherPartyRetailerUser && retailerToken) {
        if (
          !isEqual(
            newFetchState.conversationByRetailerToken.get(retailerToken),
            conversation
          )
        ) {
          const newConversationByRetailerToken = new Map(
            newFetchState.conversationByRetailerToken
          );
          newConversationByRetailerToken.set(retailerToken, conversation);
          newFetchState = {
            ...newFetchState,
            conversationByRetailerToken: newConversationByRetailerToken,
          };
        }
      }

      return newFetchState;
    },
    []
  );

  const storeUpdatedConversation = useCallback(
    (updatedConversation: ConversationModel): void => {
      if (
        !setAllMainStreamFetchState ||
        !setUnreadOnlyMainStreamFetchState ||
        !setStarredByBrandOnlyMainStreamFetchState
      ) {
        return;
      }

      setAllMainStreamFetchState((allMainStreamFetchState) =>
        updateConversationInFetchState(
          allMainStreamFetchState,
          updatedConversation
        )
      );

      setUnreadOnlyMainStreamFetchState((unreadOnlyMainStreamFetchState) => {
        if (
          existsInFetchState(
            unreadOnlyMainStreamFetchState,
            updatedConversation
          ) ||
          isConversationModelUnRead(updatedConversation)
        ) {
          return updateConversationInFetchState(
            unreadOnlyMainStreamFetchState,
            updatedConversation
          );
        } else {
          return unreadOnlyMainStreamFetchState;
        }
      });

      setStarredByBrandOnlyMainStreamFetchState(
        (starredByBrandOnlyMainStreamFetchState) => {
          if (
            existsInFetchState(
              starredByBrandOnlyMainStreamFetchState,
              updatedConversation
            ) ||
            isConversationModelStarredByBrand(updatedConversation)
          ) {
            return updateConversationInFetchState(
              starredByBrandOnlyMainStreamFetchState,
              updatedConversation
            );
          } else {
            return starredByBrandOnlyMainStreamFetchState;
          }
        }
      );
    },
    [
      updateConversationInFetchState,
      existsInFetchState,
      setAllMainStreamFetchState,
      setUnreadOnlyMainStreamFetchState,
      setStarredByBrandOnlyMainStreamFetchState,
    ]
  );

  const removeRetailerContextConversationIfEmpty = useCallback(
    (retailerToken: string) => {
      if (
        !setAllMainStreamFetchState ||
        !setUnreadOnlyMainStreamFetchState ||
        !setStarredByBrandOnlyMainStreamFetchState
      ) {
        return;
      }

      setAllMainStreamFetchState((allMainStreamFetchState) => {
        const conversation =
          allMainStreamFetchState.conversationByRetailerToken.get(
            retailerToken
          );
        if (
          conversation &&
          getConversationModelMessageCount(conversation) === 0
        ) {
          const newConversationByRetailerToken = new Map(
            allMainStreamFetchState.conversationByRetailerToken
          );
          newConversationByRetailerToken.delete(retailerToken);
          const newConversationByUserToken = new Map(
            allMainStreamFetchState.conversationByUserToken
          );
          newConversationByUserToken.delete(
            getConversationModelOtherPartyUserToken(conversation)
          );
          return {
            ...allMainStreamFetchState,
            conversationByRetailerToken: newConversationByRetailerToken,
            conversationByUserToken: newConversationByUserToken,
          };
        } else {
          return allMainStreamFetchState;
        }
      });

      setUnreadOnlyMainStreamFetchState((unreadOnlyMainStreamFetchState) => {
        const conversation =
          unreadOnlyMainStreamFetchState.conversationByRetailerToken.get(
            retailerToken
          );
        if (
          conversation &&
          getConversationModelMessageCount(conversation) === 0
        ) {
          const newConversationByRetailerToken = new Map(
            unreadOnlyMainStreamFetchState.conversationByRetailerToken
          );
          newConversationByRetailerToken.delete(retailerToken);
          const newConversationByUserToken = new Map(
            unreadOnlyMainStreamFetchState.conversationByUserToken
          );
          newConversationByUserToken.delete(
            getConversationModelOtherPartyUserToken(conversation)
          );
          return {
            ...unreadOnlyMainStreamFetchState,
            conversationByRetailerToken: newConversationByRetailerToken,
            conversationByUserToken: newConversationByUserToken,
          };
        } else {
          return unreadOnlyMainStreamFetchState;
        }
      });

      setStarredByBrandOnlyMainStreamFetchState(
        (starredByBrandOnlyMainStreamFetchState) => {
          const conversation =
            starredByBrandOnlyMainStreamFetchState.conversationByRetailerToken.get(
              retailerToken
            );
          if (
            conversation &&
            getConversationModelMessageCount(conversation) === 0
          ) {
            const newConversationByRetailerToken = new Map(
              starredByBrandOnlyMainStreamFetchState.conversationByRetailerToken
            );
            newConversationByRetailerToken.delete(retailerToken);
            const newConversationByUserToken = new Map(
              starredByBrandOnlyMainStreamFetchState.conversationByUserToken
            );
            newConversationByUserToken.delete(
              getConversationModelOtherPartyUserToken(conversation)
            );
            return {
              ...starredByBrandOnlyMainStreamFetchState,
              conversationByRetailerToken: newConversationByRetailerToken,
              conversationByUserToken: newConversationByUserToken,
            };
          } else {
            return starredByBrandOnlyMainStreamFetchState;
          }
        }
      );
    },
    [
      setAllMainStreamFetchState,
      setUnreadOnlyMainStreamFetchState,
      setStarredByBrandOnlyMainStreamFetchState,
    ]
  );

  const removeBrandContextConversationIfEmpty = useCallback(
    (brandToken: string) => {
      if (
        !setAllMainStreamFetchState ||
        !setUnreadOnlyMainStreamFetchState ||
        !setStarredByBrandOnlyMainStreamFetchState
      ) {
        return;
      }

      setAllMainStreamFetchState((allMainStreamFetchState) => {
        const conversation =
          allMainStreamFetchState.conversationByBrandToken.get(brandToken);
        if (
          conversation &&
          getConversationModelMessageCount(conversation) === 0
        ) {
          const newConversationByBrandToken = new Map(
            allMainStreamFetchState.conversationByBrandToken
          );
          newConversationByBrandToken.delete(brandToken);
          const newConversationByUserToken = new Map(
            allMainStreamFetchState.conversationByUserToken
          );
          newConversationByUserToken.delete(
            getConversationModelOtherPartyUserToken(conversation)
          );
          return {
            ...allMainStreamFetchState,
            conversationByBrandToken: newConversationByBrandToken,
            conversationByUserToken: newConversationByUserToken,
          };
        } else {
          return allMainStreamFetchState;
        }
      });

      setUnreadOnlyMainStreamFetchState((unreadOnlyMainStreamFetchState) => {
        const conversation =
          unreadOnlyMainStreamFetchState.conversationByBrandToken.get(
            brandToken
          );
        if (
          conversation &&
          getConversationModelMessageCount(conversation) === 0
        ) {
          const newConversationByBrandToken = new Map(
            unreadOnlyMainStreamFetchState.conversationByBrandToken
          );
          newConversationByBrandToken.delete(brandToken);
          const newConversationByUserToken = new Map(
            unreadOnlyMainStreamFetchState.conversationByUserToken
          );
          newConversationByUserToken.delete(
            getConversationModelOtherPartyUserToken(conversation)
          );
          return {
            ...unreadOnlyMainStreamFetchState,
            conversationByBrandToken: newConversationByBrandToken,
            conversationByUserToken: newConversationByUserToken,
          };
        } else {
          return unreadOnlyMainStreamFetchState;
        }
      });

      setStarredByBrandOnlyMainStreamFetchState(
        (starredByBrandOnlyMainStreamFetchState) => {
          const conversation =
            starredByBrandOnlyMainStreamFetchState.conversationByBrandToken.get(
              brandToken
            );
          if (
            conversation &&
            getConversationModelMessageCount(conversation) === 0
          ) {
            const newConversationByBrandToken = new Map(
              starredByBrandOnlyMainStreamFetchState.conversationByBrandToken
            );
            newConversationByBrandToken.delete(brandToken);
            const newConversationByUserToken = new Map(
              starredByBrandOnlyMainStreamFetchState.conversationByUserToken
            );
            newConversationByUserToken.delete(
              getConversationModelOtherPartyUserToken(conversation)
            );
            return {
              ...starredByBrandOnlyMainStreamFetchState,
              conversationByBrandToken: newConversationByBrandToken,
              conversationByUserToken: newConversationByUserToken,
            };
          } else {
            return starredByBrandOnlyMainStreamFetchState;
          }
        }
      );
    },
    [
      setAllMainStreamFetchState,
      setUnreadOnlyMainStreamFetchState,
      setStarredByBrandOnlyMainStreamFetchState,
    ]
  );

  return useMemo(
    () => ({
      storeUpdatedConversation,
      removeRetailerContextConversationIfEmpty,
      removeBrandContextConversationIfEmpty,
      updateConversationInFetchState,
      existsInFetchState,
    }),
    [
      storeUpdatedConversation,
      removeRetailerContextConversationIfEmpty,
      removeBrandContextConversationIfEmpty,
      updateConversationInFetchState,
      existsInFetchState,
    ]
  );
};

const useMessengerStoreMainStreamFetchState = () => {
  const {
    allMainStreamFetchState,
    unreadOnlyMainStreamFetchState,
    starredByBrandOnlyMainStreamFetchState,
    showOnlyUnreadConversations,
    showOnlyStarredByBrandConversations,
  } = useMessengerContext() ?? {};

  const mainStreamFetchState = useMemo(() => {
    if (showOnlyUnreadConversations) {
      return unreadOnlyMainStreamFetchState;
    } else if (showOnlyStarredByBrandConversations) {
      return starredByBrandOnlyMainStreamFetchState;
    }
    return allMainStreamFetchState;
  }, [
    showOnlyUnreadConversations,
    showOnlyStarredByBrandConversations,
    allMainStreamFetchState,
    unreadOnlyMainStreamFetchState,
    starredByBrandOnlyMainStreamFetchState,
  ]);

  return useMemo(() => ({ mainStreamFetchState }), [mainStreamFetchState]);
};
